import { useEffect, useRef, useState } from "react";
import type { Bank, ValidationError } from "../api";
import { validateQr } from "../api";
import BankLogo from "./BankLogo";
import { formatAccountNumberDisplay, formatAmount, parseAmountInput, stripSpaces } from "../format";

type Props = {
  bank: Bank;
  onChangeBank: () => void;
  accountNumber: string;
  onAccountNumberChange: (v: string) => void;
  amount: number | undefined;
  onAmountChange: (v: number | undefined) => void;
  purpose: string;
  onPurposeChange: (v: string) => void;
  onContinue: () => void;
};

function fieldError(errors: ValidationError[], field: string): string | undefined {
  return errors.find((e) => e.field === field)?.message;
}

export default function InfoStep({
  bank,
  onChangeBank,
  accountNumber,
  onAccountNumberChange,
  amount,
  onAmountChange,
  purpose,
  onPurposeChange,
  onContinue,
}: Props) {
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [checking, setChecking] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!accountNumber) {
      setErrors([]);
      setNetworkError(null);
      return;
    }

    const id = ++requestId.current;
    setChecking(true);
    setNetworkError(null);

    const timer = setTimeout(() => {
      validateQr({ bankBin: bank.bin, accountNumber, amount, purpose: purpose || undefined })
        .then((res) => {
          if (id !== requestId.current) return;
          setErrors(res.errors);
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setNetworkError("Mạng đang chập chờn, mình chưa kiểm tra được thông tin này. Thử lại giúp mình nhé.");
        })
        .finally(() => {
          if (id !== requestId.current) return;
          setChecking(false);
        });
    }, 400);

    return () => clearTimeout(timer);
  }, [bank.bin, accountNumber, amount, purpose]);

  const accountError = fieldError(errors, "accountNumber");
  const amountError = fieldError(errors, "amount");
  const purposeError = fieldError(errors, "purpose");

  const canContinue = accountNumber.trim().length > 0 && !accountError && !checking;

  return (
    <section className="step" aria-labelledby="step2-title">
      <button type="button" className="link-back" onClick={onChangeBank}>
        ← Đổi ngân hàng
      </button>

      <div className="selected-bank-chip">
        <BankLogo bank={bank} size={40} />
        <strong>{bank.shortName}</strong>
      </div>

      <h1 id="step2-title" className="step-title">
        Nhập thông tin nhận tiền
      </h1>

      <div className="field">
        <label htmlFor="account-number">Số tài khoản</label>
        <input
          id="account-number"
          className={`input input-lg ${accountError ? "input-error" : ""}`}
          inputMode="numeric"
          autoComplete="off"
          placeholder="Ví dụ: 0123456789"
          value={formatAccountNumberDisplay(accountNumber)}
          onChange={(e) => onAccountNumberChange(stripSpaces(e.target.value).replace(/\D/g, ""))}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (text) {
              e.preventDefault();
              onAccountNumberChange(stripSpaces(text).replace(/\D/g, ""));
            }
          }}
          aria-invalid={Boolean(accountError)}
          aria-describedby="account-number-hint"
        />
        <p id="account-number-hint" className={`field-hint ${accountError ? "field-hint-error" : ""}`}>
          {accountError ??
            (accountNumber.length === 0
              ? "Bạn nhập số tài khoản để mình tạo mã QR nhận tiền cho bạn nhé."
              : checking
                ? "Đang kiểm tra…"
                : "Dán hoặc gõ đúng số tài khoản ngân hàng của bạn.")}
        </p>
      </div>

      <div className="field">
        <label htmlFor="amount">Số tiền (tuỳ chọn)</label>
        <div className="amount-input-wrap">
          <input
            id="amount"
            className={`input input-lg ${amountError ? "input-error" : ""}`}
            inputMode="numeric"
            placeholder="0"
            value={amount === undefined ? "" : formatAmount(amount)}
            onChange={(e) => onAmountChange(parseAmountInput(e.target.value))}
            aria-invalid={Boolean(amountError)}
            aria-describedby="amount-hint"
          />
          <span className="amount-suffix">₫</span>
        </div>
        <p id="amount-hint" className={`field-hint ${amountError ? "field-hint-error" : ""}`}>
          {amountError ?? "Để trống nếu muốn người gửi tự nhập số tiền."}
        </p>
      </div>

      <div className="field">
        <label htmlFor="purpose">Nội dung chuyển khoản (tuỳ chọn)</label>
        <input
          id="purpose"
          className={`input ${purposeError ? "input-error" : ""}`}
          placeholder="Ví dụ: Thanh toan don hang"
          value={purpose}
          onChange={(e) => onPurposeChange(e.target.value)}
          aria-invalid={Boolean(purposeError)}
          aria-describedby="purpose-hint"
        />
        <p id="purpose-hint" className={`field-hint ${purposeError ? "field-hint-error" : ""}`}>
          {purposeError ?? "Có thể để trống."}
        </p>
      </div>

      {networkError ? <p className="field-hint field-hint-error">{networkError}</p> : null}

      <div className="bottom-bar">
        <button type="button" className="btn btn-primary btn-block" disabled={!canContinue} onClick={onContinue}>
          Tiếp tục
        </button>
      </div>
    </section>
  );
}
